import { AccessibleClickDirective } from '../../directives/accessible-click.directive';
import { InlineDialogComponent } from '../dialog/inline-dialog/inline-dialog.component';
import {
  Component,
  computed,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';

import { animate, style, transition, trigger } from '@angular/animations';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { UserGuidanceService } from '../../services/analytics/user-guidance.service';
import { IcPlaneComponent } from '../Icons/ic-plane/ic-plane';
import { IcSparklesComponent } from '../Icons/ic-sparkles/ic-sparkles';
import { IcDeclarationTemplate } from '../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { DialogService } from '../../../core/services/dialog';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { ImagePreviewComponent } from '../image-preview/image-preview.component';

interface Step {
  id: number;
  title: string;
  description: string;
  assistantMessage: string;
  actionInstruction?: string;
  actionLocation?: string;
  navigationUrl?: string;
  minimizeOnNavigate?: boolean;
  image?: string;
}

interface Tutorial {
  id: string;
  title: string;
  description: string;
  iconType?: 'sparkles' | 'templates' | 'plane';
  steps: Step[];
}

/**
 * AIAssistantBotComponent component.
 * Provides UI presentation and event handling for aiassistant bot component.
 */
@Component({
  selector: 'app-ai-assistant-bot',
  standalone: true,
  imports: [
    AccessibleClickDirective,
    InlineDialogComponent,
    IcSparklesComponent,
    IcPlaneComponent,
    IcDeclarationTemplate,
    TranslatePipe,
  ],
  templateUrl: './ai-assistant-bot.component.html',
  styleUrls: ['./ai-assistant-bot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '300ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class AIAssistantBotComponent {
  /** Injected service for router. */
  private router = inject(Router);
  /** Injected service for guidance service. */
  private guidanceService = inject(UserGuidanceService);
  /** Injected service for dialog service. */
  private dialogService = inject(DialogService);
  /** Active language. Reading it keeps translated copy in sync without checking this view on every pass. */
  private readonly translationService = inject(TranslationService);
  /**
   * Input property for visible.
   */
  visible = input<boolean>(false);
  /**
   * Event emitter for visible change changes.
   */
  visibleChange = output<boolean>();

  // All state lives in the service so it persists across navigation / minimize cycles
  /**
   * Active UI language. The template reads this so a language change refreshes
   * the translated copy. The view is OnPush, so unrelated updates do not
   * re-run the translate pipe (that re-run produced NG0100).
   */
  readonly uiLanguage = computed(() =>
    this.translationService.getCurrentLanguageCode()
  );

  /** Property current view. */
  readonly currentView = this.guidanceService.aiCurrentView;
  /** Property current step index. */
  readonly currentStepIndex = this.guidanceService.aiCurrentStepIndex;
  /** Property minimized. */
  readonly minimized = this.guidanceService.aiMinimized;
  /** Property selected tutorial. */
  readonly selectedTutorial = computed(
    () =>
      this.tutorials.find(
        (t) => t.id === this.guidanceService.aiSelectedTutorialId()
      ) ?? null
  );

  /** Property tutorials. */
  tutorials: Tutorial[] = [
    {
      id: 'shipment-flow',
      title: 'tutorialShipmentCreationTitle',
      description: 'tutorialShipmentFlowDesc',
      iconType: 'plane',
      steps: [
        {
          id: 1,
          title: 'tutorialStep1Title',
          description: 'tutorialStep1Desc',
          assistantMessage: 'tutorialStep1Msg',
          actionInstruction: 'tutorialStep1Inst',
          actionLocation: 'Shipments Page',
          navigationUrl: '/shipments/all?openPopup=import',
          minimizeOnNavigate: true,
          image: 'assets/images/user-guide/shipment-step-01.png',
        },
        {
          id: 2,
          title: 'tutorialStep2Title',
          description: 'tutorialStep2Desc',
          assistantMessage: 'tutorialStep2Msg',
          actionInstruction: 'tutorialStep2Inst',
          actionLocation: 'Shipments List Page',
          navigationUrl: '/shipments/all',
          image: 'assets/images/user-guide/shipment-step-02.png',
        },
        {
          id: 3,
          title: 'tutorialStep3Title',
          description: 'tutorialStep3Desc',
          assistantMessage: 'tutorialStep3Msg',
          actionLocation:
            'Declaration List / Shipment Details → Declarations Tab',
          image: 'assets/images/user-guide/shipment-step-03.png',
        },
      ],
    },
    {
      id: 'manifest-templates',
      title: 'tutorialManifestFlowTitle',
      description: 'tutorialManifestFlowDesc',
      iconType: 'templates',
      steps: [
        {
          id: 1,
          title: 'tutorialManifestStep1Title',
          description: 'tutorialManifestStep1Desc',
          assistantMessage: 'tutorialManifestStep1Msg',
          navigationUrl: '/settings/manifest-templates',
          image: 'assets/images/user-guide/manifest-step-01.png',
        },
        {
          id: 2,
          title: 'tutorialManifestStep2Title',
          description: 'tutorialManifestStep2Desc',
          assistantMessage: 'tutorialManifestStep2Msg',
          navigationUrl: '/settings/manifest-templates',
          image: 'assets/images/user-guide/manifest-step-02.png',
        },
        {
          id: 3,
          title: 'tutorialManifestStep3Title',
          description: 'tutorialManifestStep3Desc',
          assistantMessage: 'tutorialManifestStep3Msg',
          navigationUrl: '/settings/clients',
          image: 'assets/images/user-guide/manifest-step-03.png',
        },
      ],
    },
    {
      id: 'declaration-templates',
      title: 'declarationTemplates',
      description: 'tutorialDeclarationTemplatesDesc',
      iconType: 'templates',
      steps: [
        {
          id: 1,
          title: 'tutorialDeclTempStep1Title',
          description: 'tutorialDeclTempStep1Desc',
          assistantMessage: 'tutorialDeclTempStep1Msg',
          actionInstruction: 'tutorialDeclTempStep1Inst',
          actionLocation: 'Settings Page',
          navigationUrl: '/settings/declaration-templates',
          image: 'assets/images/user-guide/template-step-01.png',
        },
        {
          id: 2,
          title: 'tutorialDeclTempStep2Title',
          description: 'tutorialDeclTempStep2Desc',
          assistantMessage: 'tutorialDeclTempStep2Msg',
          actionInstruction: 'tutorialDeclTempStep2Inst',
          actionLocation: 'Declaration Templates List',
          navigationUrl: '/settings/declaration-templates',
          image: 'assets/images/user-guide/tempalate-step-02.png',
        },
        {
          id: 3,
          title: 'tutorialDeclTempStep3Title',
          description: 'tutorialDeclTempStep3Desc',
          assistantMessage: 'tutorialDeclTempStep3Msg',
          actionInstruction: 'tutorialDeclTempStep3Inst',
          actionLocation: 'Create Declaration Template',
          navigationUrl: '/settings/declaration-templates',
          image: 'assets/images/user-guide/template-step-03.png',
        },
      ],
    },
  ];

  /**
   * Handles on close logic.
   */
  onClose() {
    this.guidanceService.aiMinimized.set(false);
    this.visibleChange.emit(false);
    // Reset tutorial state after close animation
    setTimeout(() => {
      this.guidanceService.aiCurrentView.set('menu');
      this.guidanceService.aiSelectedTutorialId.set(null);
      this.guidanceService.aiCurrentStepIndex.set(0);
    }, 300);
  }

  /**
   * Handles on dialog hide logic.
   */
  onDialogHide() {
    // When dialog hides due to minimize, aiMinimized is already true — don't reset state
    if (this.guidanceService.aiMinimized()) return;
    this.visibleChange.emit(false);
  }

  /**
   * Handles minimize logic.
   */
  minimize() {
    this.guidanceService.aiMinimized.set(true);
  }

  /**
   * Handles restore logic.
   */
  restore() {
    this.guidanceService.aiMinimized.set(false);
  }

  /**
   * Handles select tutorial logic.
   * @param tutorial - The tutorial
   */
  selectTutorial(tutorial: Tutorial) {
    this.guidanceService.aiSelectedTutorialId.set(tutorial.id);
    this.guidanceService.aiCurrentStepIndex.set(0);
    this.guidanceService.aiCurrentView.set('tutorial');
  }

  /**
   * Handles back to menu logic.
   */
  backToMenu() {
    this.guidanceService.aiCurrentView.set('menu');
    this.guidanceService.aiSelectedTutorialId.set(null);
  }

  /**
   * Handles next step logic.
   */
  nextStep() {
    const tutorial = this.selectedTutorial();
    if (
      tutorial &&
      this.guidanceService.aiCurrentStepIndex() < tutorial.steps.length - 1
    ) {
      this.guidanceService.aiCurrentStepIndex.update((i) => i + 1);
    } else {
      this.onClose();
    }
  }

  /**
   * Handles prev step logic.
   */
  prevStep() {
    if (this.guidanceService.aiCurrentStepIndex() > 0) {
      this.guidanceService.aiCurrentStepIndex.update((i) => i - 1);
    }
  }

  /**
   * Handles navigate to location logic.
   * @param url - The url
   * @param step - The step
   */
  navigateToLocation(url: string | undefined, step?: Step) {
    if (url) {
      const [path, query] = url.split('?');
      const queryParams: any = {};

      if (query) {
        query.split('&').forEach((param) => {
          const [key, value] = param.split('=');
          queryParams[key] = value;
        });
      }

      this.router.navigate([path], { queryParams });

      if (step?.minimizeOnNavigate) {
        this.guidanceService.aiCurrentStepIndex.update((i) => i + 1);
        this.minimize();
      } else {
        this.onClose();
      }
    }
  }

  /**
   * Handles preview image logic.
   */
  previewImage() {
    const tutorial = this.selectedTutorial();
    const step = tutorial?.steps?.[this.currentStepIndex()];
    if (step?.image) {
      this.dialogService.open(ImagePreviewComponent, {
        header: '',
        width: '80%',
        contentStyle: {
          padding: '0',
          overflow: 'hidden',
          borderRadius: '16px',
        },
        baseZIndex: 10001,
        data: {
          src: step.image,
          filename: step.title,
        },
        showHeader: false,
        dismissableMask: true,
        closeOnEscape: true,
      });
    }
  }
}
